import { z } from 'zod';
import { requiredFieldError } from '..';
import { FINANCIAL_PRODUCT } from '../constants';
import { address, generateSchema } from '../shared';
import { ApplicantType } from '../types';

/**
 * `financeSchema` - Schema to validate FinancePurchase form.
 *
 *  Fields:
 *
 * - `repaymentTerm`: REQUIRED field for Primary Applicant
 *    Error message: 'Please select a repayment term'.
 *
 * - `address`: REQUIRED field for Primary Applicant
 *    Error message: 'Installation address is a required field'.
 *
 * - `totalMinimumPayable`: OPTIONAL field for Primary Applicant
 *
 * - `payment`: REQUIRED field for Primary Applicant
 *
 */

const defaultApplicantObject = {
  ownProperty: z.boolean(),
  nonHomeOwnerCheck: z.boolean().optional(),
};
const defaultSchema = {
  applicantType: z.union([z.literal(ApplicantType.Primary), z.literal(ApplicantType.Joint)]),
  repaymentTerm: z
    .union([
      z.string({ required_error: 'Please select a repayment term' }),
      z.number({ required_error: 'Please select a repayment term' }),
    ])
    .optional(),
  totalMinimumPayable: z.number().optional(),
  address: address.optional(),
  applicant: z
    .object({
      ...defaultApplicantObject,
    })
    .optional(),
  payment: z
    .object({
      isCashApplication: z.boolean({ required_error: 'Please select a payment method' }),
    })
    .optional(),
};

const schemaWithPreviousAddress = {
  ...defaultSchema,
  applicant: z
    .object({
      ...defaultApplicantObject,
      isCurrentAddress: z.boolean().optional(),
      previousAddress: address.optional(),
    })
    .optional(),
};

const { applicant, ...defaultSchemaWithoutOwnProperty } = defaultSchema;

const customSchemas = {
  [FINANCIAL_PRODUCT.ACT_SFA]: defaultSchemaWithoutOwnProperty,
  [FINANCIAL_PRODUCT.BRIGHTE_PAY]: schemaWithPreviousAddress,
  [FINANCIAL_PRODUCT.SME]: defaultSchemaWithoutOwnProperty,
  [FINANCIAL_PRODUCT.ACT_COMMUNITY]: defaultSchemaWithoutOwnProperty,
};

export const financeSchema = generateSchema<typeof customSchemas, typeof defaultSchema>({
  customSchemas,
  defaultSchema,
}).superRefine((formValues, ctx) => {
  const addIssue = (path: string[], message: string) => {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message,
      path,
    });
  };

  // Validate fields for Primary Applicant
  if (formValues.applicantType === ApplicantType.Primary) {
    if (formValues.payment?.isCashApplication === undefined) {
      addIssue(['payment', 'isCashApplication'], 'Please select a payment method');
    }

    if (!formValues.repaymentTerm) {
      addIssue(['repaymentTerm'], 'Please select a repayment term');
    }

    if (!formValues.address) {
      addIssue(['address'], 'Installation address is a required field');
    }

    // Validate nonHomeOwnerCheck(required) if ownProperty is false
    if (formValues.applicant?.ownProperty === false && formValues.applicant?.nonHomeOwnerCheck === undefined) {
      addIssue(['applicant', 'nonHomeOwnerCheck'], requiredFieldError());
    }

    if (
      formValues.applicant?.isCurrentAddress === undefined &&
      [FINANCIAL_PRODUCT.BRIGHTE_PAY].includes(formValues.financialProductId as string)
    ) {
      addIssue(['applicant', 'isCurrentAddress'], requiredFieldError());
    }

    if (formValues.applicant?.isCurrentAddress === false && !formValues.applicant?.previousAddress) {
      addIssue(['applicant.previousAddress'], 'Previous address is a required field');
    }
  }

  // Validate fields for Joint Applicant
  const businessProducts = [FINANCIAL_PRODUCT.ACT_SFA, FINANCIAL_PRODUCT.SME, FINANCIAL_PRODUCT.ACT_COMMUNITY];
  if (
    formValues.applicant?.ownProperty === undefined &&
    !businessProducts.includes(formValues.financialProductId as string)
  ) {
    addIssue(['applicant', 'ownProperty'], requiredFieldError());
  }
});

export type FinancePurchaseType = z.infer<typeof financeSchema>;
